import { APPLICATION_STEPS } from '@utils';

export const ASSISTANT_FLAG_KEY = 'enable-application-assistant';

// Iteration 1 (plan v3): the assistant mounts on the income step only.
// Whole-form rollout is a change to this list once thresholds prove out.
export const ASSISTANT_ENABLED_STEPS: string[] = [APPLICATION_STEPS.INCOME_VERIFICATION];

/**
 * Narrowest viewport the assistant is offered on. The panel sits beside the form as a fixed
 * column; below this it would cover the step it is meant to help with.
 */
export const ASSISTANT_MIN_VIEWPORT_PX = 1024;

export const JOURNAL_MAX_EVENTS = 50;

// Struggle scoring: weights per event type and the two launcher thresholds.
export const STRUGGLE_WEIGHTS: Record<string, number> = {
  validation_failed: 1,
  rapid_clicks: 2,
  save_error: 3,
  verification_failed: 3,
};
export const ATTENTION_THRESHOLD = 3;
export const PROACTIVE_THRESHOLD = 6;

// Rapid-click detection: this many clicks on the same target inside the window.
export const RAPID_CLICK_COUNT = 3;
export const RAPID_CLICK_WINDOW_MS = 2000;

/**
 * Where the customer is *within* a step. The income step reports this as it changes, so the
 * suggested questions can follow what is actually on screen rather than the step as a whole.
 *
 * Kept separate from the form-field reader in `AssistantFormFields`: that answers what the
 * customer has filled in and is read only when a message is sent, whereas this answers which
 * part of the step is on screen — which depends on a feature flag and a bank-connection query
 * as well as the form — and has to re-render the panel the moment it changes.
 */
export const ASSISTANT_SUB_STATES = {
  INCOME_DEFAULT: 'income.default',
  INCOME_EMPLOYED: 'income.employed',
  INCOME_SELF_EMPLOYED: 'income.selfEmployed',
  INCOME_RETIRED: 'income.retired',
  INCOME_ADDITIONAL: 'income.additional',
  VERIFY_CHOOSING: 'verify.choosing',
  VERIFY_CDR: 'verify.cdr',
  VERIFY_MANUAL_EMPLOYED: 'verify.manual.employed',
  VERIFY_MANUAL_SELF_EMPLOYED: 'verify.manual.selfEmployed',
  VERIFY_MANUAL_RETIRED: 'verify.manual.retired',
} as const;

export type AssistantSubState = (typeof ASSISTANT_SUB_STATES)[keyof typeof ASSISTANT_SUB_STATES];

/**
 * Suggested questions per sub-state, falling back to the step-level entry when the step has not
 * reported one (or has reported one with no copy of its own).
 */
export const QUICK_ACTIONS: Record<string, string[]> = {
  [APPLICATION_STEPS.INCOME_VERIFICATION]: ['Which income type fits me?', 'Do I enter my income before or after tax?'],

  [ASSISTANT_SUB_STATES.INCOME_DEFAULT]: ['Which income type fits me?', 'Do I enter my income before or after tax?'],
  [ASSISTANT_SUB_STATES.INCOME_EMPLOYED]: ['My job isn’t listed', 'Why do you need my employers number?'],
  [ASSISTANT_SUB_STATES.INCOME_SELF_EMPLOYED]: ['My job isn’t listed', 'I can’t find my business'],
  [ASSISTANT_SUB_STATES.INCOME_RETIRED]: ['Which pension type should I pick?', 'What counts as my primary income?'],
  [ASSISTANT_SUB_STATES.INCOME_ADDITIONAL]: [
    'I need help with verifying my income',
    'What counts as additional income?',
  ],
  [ASSISTANT_SUB_STATES.VERIFY_CHOOSING]: ['Why do I need to verify my income?', 'Which option is quicker?'],
  [ASSISTANT_SUB_STATES.VERIFY_CDR]: ['What will you see in my account?', 'Who is Fiskil?'],
  [ASSISTANT_SUB_STATES.VERIFY_MANUAL_EMPLOYED]: ['Which payslips do I need?', 'Can I use a bank statement?'],
  [ASSISTANT_SUB_STATES.VERIFY_MANUAL_SELF_EMPLOYED]: ['Which tax document do I need?', 'Can I use a bank statement?'],
  [ASSISTANT_SUB_STATES.VERIFY_MANUAL_RETIRED]: ['What statement do I upload?', 'Can I use a bank statement?'],
};

export const PROACTIVE_OFFER_COPY: Record<string, string> = {
  [APPLICATION_STEPS.INCOME_VERIFICATION]: 'Stuck on income verification? I can help with what’s accepted.',
};

export const ASSISTANT_RATING_PROMPT = 'How helpful was this?';
export const ASSISTANT_RATING_ANCHORS: [string, string] = ['Not helpful', 'Very helpful'];
export const ASSISTANT_RATING_THANKS = 'Thanks — that helps us improve this.';
export const ASSISTANT_RATING_SCALE = 5;

// Pending Compliance review; must be approved before the assistant is enabled for customers.
/**
 * Asked on the customer's behalf when an error hands off to the panel. The generic default suits
 * any error; call sites that know the failure is an upload pass ERROR_HANDOFF_QUESTIONS.upload so
 * the question reads the way the customer would have typed it.
 */
export const ERROR_HANDOFF_QUESTIONS = {
  default: 'Explain what happened, and how do I fix it?',
  upload: 'Why did my document upload fail?',
} as const;

/** The error → assistant hand-off, split so the link text alone is the accessible name. */
export const ASSISTANT_HANDOFF_PROMPT = 'Not sure what’s wrong?';
export const ASSISTANT_HANDOFF_LINK = 'Ask our assistant';

export const ASSISTANT_DISCLAIMER = 'AI can make mistakes. Not financial advice.';
export const ASSISTANT_GREETING =
  'Hi, I can help with any questions about the income step. What would you like to know?';
export const ASSISTANT_RATE_LIMIT_REPLY =
  'I\u2019m not able to answer any more questions right now. Please reach out to the Brighte support team using the support number on this page and they\u2019ll be able to help.';
export const ASSISTANT_FALLBACK_REPLY = 'Sorry — I couldn’t put an answer together just then. Please try again.';
